import { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import currentVersion, { release } from './version';
import { fetchBackendVersion } from './backendVersion';

/**
 * Two different problems, deliberately reported differently.
 *
 *   Stale tab  — a newer front end has been deployed and this tab is still
 *                running the old bundle. A refresh fixes it, so offer one.
 *
 *   Skew       — the backend is running a different release from this front
 *                end. Three deployables ship separately and the deploys are
 *                not atomic, so this is a real state, and **a refresh will not
 *                fix it**. Saying "refresh" here would send someone round a
 *                loop that cannot terminate.
 */
export default function VersionChecker() {
  const [stale, setStale] = useState(false);
  const [skew, setSkew] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        const data = await res.json();
        if (data.version !== currentVersion) setStale(true);
      } catch (err) {
        console.warn('Version check failed:', err);
      }

      // A failed call leaves this null, not "skewed" — a network blip must not
      // look like a bad deploy.
      const backend = await fetchBackendVersion();
      setSkew(backend && backend.release && backend.release !== release ? backend.release : null);
    };

    check();
    const interval = setInterval(check, 60000); // every minute
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => { window.location.reload(true); };

  // Skew is the more serious of the two and the one nobody is looking for, so
  // it wins the slot when both are true.
  if (skew) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="warning">
          This screen is {release} but the server is running {skew}. A deploy may be
          part-finished — please tell the office before relying on anything here.
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Snackbar open={stale} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Refresh
          </Button>
        }
      >
        A new version is available.
      </Alert>
    </Snackbar>
  );
}
